import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { Typography } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { httpClient } from '@web/services/singletons';
import { withCdnEntryFile } from '@web/vin-sessions/utils/open-document-tab';

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 16,
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 16,
        padding: 40,
        textAlign: 'center',
    },
});

/**
 * Handles the return trip for a bookmarked CDN document URL. When the CloudFront
 * signed cookie for a document expires, CloudFront redirects the bookmark back to
 * our app domain at /doc/<docpath> or /techshare-docs/<docpath>. This component
 * takes that relative path, re-resolves it via /cdn-url-by-path (which re-issues
 * the CloudFront cookies) and redirects the browser to the refreshed CDN URL.
 * Login-on-expiry is handled upstream by the auth guard in AppNavigator.
 */
export const CdnRedirectHandler: FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const { token } = useAuthState();
    const [error, setError] = useState<string | null>(null);
    const hasRedirected = useRef(false);

    useEffect(() => {
        if (hasRedirected.current) {
            return;
        }
        hasRedirected.current = true;

        const performRedirect = async () => {
            try {
                const storedPath = sessionStorage.getItem('cdn_redirect_path');
                sessionStorage.removeItem('cdn_redirect_path');

                // CloudFront sends an expired-cookie bookmark here two ways:
                //  - new:    /redirect/stis?path=<url-encoded doc path>  → read the `path` query param
                //  - legacy: /doc/<...> or /techshare-docs/<...>         → the pathname is the doc path
                // The backend expects the relative doc path keeping its doc/ or techshare-docs/
                // prefix; a leading slash is tolerated, but we strip it for cleanliness.
                const source = storedPath ?? location.pathname + location.search;
                const [sourcePath, sourceQuery = ''] = source.split('?');

                let path: string;
                if (sourcePath.startsWith('/redirect/stis')) {
                    path = new URLSearchParams(sourceQuery).get('path') ?? '';
                } else {
                    path = source;
                }
                path = path.replace(/^\//, '');

                if (!path || path === '/') {
                    setError('Invalid redirect path. No document path was provided.');
                    return;
                }

                // HTML Diagnostics documents are multi-file bundles with deep paths
                // (doc/htmlDiagnostics/<folder>/contents/lookup.html). The WAF blocks those deep
                // paths in the query string, so for HTML Diagnostics ONLY we send just the first
                // three segments (doc / htmlDiagnostics / <dynamic folder>) to the backend and
                // re-append the remaining sub-path to the returned CDN URL client-side. Every other
                // document type is a single file and is sent through unchanged.
                const segments = path.split('/');
                const isHtmlDiagnostics = segments[1] === 'htmlDiagnostics';
                const basePath = isHtmlDiagnostics ? segments.slice(0, 3).join('/') : path;
                const remainder = isHtmlDiagnostics ? segments.slice(3).join('/') : '';

                const res = await fetch(
                    `${httpClient.vinUrl}/docs/cdn-url-by-path?path=${encodeURIComponent(basePath)}`,
                    {
                        headers: {
                            'X-Subcomm-Access-Token': token,
                        },
                        credentials: 'include',
                    }
                );

                if (!res.ok) {
                    setError('The document could not be found for the given path.');
                    return;
                }

                const data = await res.json();

                if (data?.documentUrl) {
                    // Re-attach the sub-path the WAF forced us to strip from the request; if there's
                    // no remaining part, default the bundle entry to index.html (withCdnEntryFile).
                    const finalUrl = remainder
                        ? `${data.documentUrl.replace(/\/$/, '')}/${remainder}`
                        : data.documentUrl;
                    window.location.href = withCdnEntryFile(finalUrl) ?? finalUrl;
                } else {
                    setError('The document could not be found for the given path.');
                }
            } catch (err) {
                logger.error('Failed to perform the CDN redirect', { error: err });
                setError('An error occurred while looking up the document. Please try again later.');
            }
        };

        performRedirect();
    }, [location]);

    if (error) {
        return (
            <div className={classes.errorContainer}>
                <Typography variant="h3">{error}</Typography>
                <Typography variant="labelRegular" color="blueOne" onPress={() => navigate('/', { replace: true })}>
                    Go to Home
                </Typography>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Typography variant="h3">Redirecting to Document...</Typography>
            <Typography variant="labelRegular">
                Please wait while we look up the document. You will be redirected shortly.
            </Typography>
        </div>
    );
};
